import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { restoreSelection } from "@redux/slices/tenantSlice";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import {
  addViews,
  claimViews,
  clearViews,
  moveView as moveViewAction,
  removeView,
  setActiveView,
  setViewLabel,
  setViewSelection,
  setViewUrl,
} from "./viewsSlice";
import {
  MAX_VIEWS,
  isViewablePath,
  leavesSingleView,
  neighbourOnClose,
  planOpenView,
  sameSelection,
  snapshotSelection,
  splitUrl,
  viewForLocation,
  viewIdentity,
  type View,
} from "./viewsModel";
import { resolveViewLabel } from "./viewLabels";
import ViewContextMenu, { type ViewMenuAnchor } from "./ViewContextMenu";

/**
 * The attribute that makes an element a way into a view: the address it navigates to.
 * The element keeps its own onClick for plain clicks; this provider adds, for any
 * element carrying it, the explicit gestures that open a view instead — right click
 * ("Add view"), Cmd/Ctrl+click, middle click, and Shift+F10 / the Menu key.
 *
 * One attribute rather than a wrapper component because the entry points are ~70
 * buttons, table cells and menu links of every shape; marking them costs one prop and
 * leaves their markup and click behaviour exactly as they were.
 */
export const VIEW_HREF_ATTR = "data-view-href";

/** Spread onto an element that navigates to `url`. */
export function viewHref(url: string | null | undefined): { [VIEW_HREF_ATTR]?: string } {
  return url ? { [VIEW_HREF_ATTR]: url } : {};
}

export interface ViewFlash {
  id: string;
  /** Changes on every flash, so the same view can glow twice in a row. */
  nonce: number;
}

interface ViewsContextValue {
  views: View[];
  activeId: string | null;
  /**
   * Adds a view for `url`. `focus` takes the user there — what "Add view" in the menu does;
   * without it the view opens in the background, the way Cmd/Ctrl+click and middle click
   * open a browser tab.
   */
  openInView: (url: string, options?: { focus?: boolean }) => void;
  activateView: (id: string) => void;
  closeView: (id: string) => void;
  moveView: (id: string, toIndex: number) => void;
  flash: ViewFlash | null;
  notice: string | null;
  dismissNotice: () => void;
}

const ViewsContext = createContext<ViewsContextValue | null>(null);

const noop = () => undefined;

/** Outside the provider (a header rendered on its own, in a test): no views, inert actions. */
const NO_VIEWS: ViewsContextValue = {
  views: [],
  activeId: null,
  openInView: noop,
  activateView: noop,
  closeView: noop,
  moveView: noop,
  flash: null,
  notice: null,
  dismissNotice: noop,
};

export function useViews(): ViewsContextValue {
  return useContext(ViewsContext) ?? NO_VIEWS;
}

const FLASH_MS = 2000;
const NOTICE_MS = 5000;

function makeViewId(): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `view-${Date.now().toString(36)}-${random}`;
}

function viewTargetFrom(target: EventTarget | null): { element: HTMLElement; href: string } | null {
  if (!(target instanceof Element)) return null;
  const element = target.closest<HTMLElement>(`[${VIEW_HREF_ATTR}]`);
  const href = element?.getAttribute(VIEW_HREF_ATTR);
  if (!element || !href) return null;
  // A way into a screen that cannot be a view (Settings, Admin Center) keeps the
  // browser's own behaviour for every gesture.
  if (!isViewablePath(splitUrl(href).pathname)) return null;
  return { element, href };
}

/** Text the user selected on purpose inside the element: right click must still copy it. */
function hasSelectionInside(element: HTMLElement): boolean {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || !selection.toString().trim()) return false;
  return selection.anchorNode ? element.contains(selection.anchorNode) : false;
}

export function ViewsProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const api = useAxiosAuth0();

  const { items: views, activeId } = useAppSelector((state) => state.views);
  const selectionState = useAppSelector((state) => state.selection);
  const selection = useMemo(() => snapshotSelection(selectionState), [selectionState]);
  const ownerKey = useAppSelector(
    (state) => state.userProfile.email || (state.userProfile.id ? String(state.userProfile.id) : "")
  );

  const url = `${location.pathname}${location.search}`;
  const isViewable = isViewablePath(location.pathname);

  // Latest values for the document-level listeners and the effects below, which must
  // not re-subscribe (or re-run) on every change of what they read.
  const latest = useRef({ views, activeId, selection, url, isViewable });
  latest.current = { views, activeId, selection, url, isViewable };

  const [flash, setFlash] = useState<ViewFlash | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [menu, setMenu] = useState<ViewMenuAnchor | null>(null);

  const flashView = useCallback((id: string) => {
    setFlash({ id, nonce: Date.now() });
  }, []);

  useEffect(() => {
    if (!flash) return undefined;
    const timer = window.setTimeout(() => setFlash(null), FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flash]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  // ---------------------------------------------------------------- actions

  const activateView = useCallback(
    (id: string) => {
      const now = latest.current;
      const view = now.views.find((v) => v.id === id);
      if (!view) return;
      if (id === now.activeId && view.url === now.url) return;
      // One batch: the lens and the address change in the same render, so the page
      // being left never sees the new tenant (the detail views leave for their list
      // when the tenant changes under them) and the page arrived at never sees the old.
      dispatch(setActiveView(id));
      if (!sameSelection(view.selection, now.selection)) dispatch(restoreSelection(view.selection));
      if (view.url !== now.url) navigate(view.url);
    },
    [dispatch, navigate]
  );

  const openInView = useCallback(
    (href: string, { focus = false }: { focus?: boolean } = {}) => {
      const now = latest.current;
      if (!isViewablePath(splitUrl(href).pathname)) return;
      const plan = planOpenView(
        now.views,
        { url: href, selection: now.selection, current: now.isViewable ? { url: now.url } : null },
        makeViewId
      );
      if (plan.kind === "full") {
        setNotice(`You have ${MAX_VIEWS} views open. Close one to add another.`);
        return;
      }
      if (plan.kind === "exists") {
        if (focus) activateView(plan.flashId);
        flashView(plan.flashId);
        return;
      }
      dispatch(addViews(plan.add));
      const target = plan.add.find((view) => view.id === plan.flashId);
      if (focus && target) {
        // The new view carries the lens the gesture happened under, which is the one in
        // use, so only the address has to move.
        dispatch(setActiveView(target.id));
        if (target.url !== now.url) navigate(target.url);
      } else if (plan.activeId) {
        dispatch(setActiveView(plan.activeId));
      }
      flashView(plan.flashId);
    },
    [dispatch, flashView, activateView, navigate]
  );

  const closeView = useCallback(
    (id: string) => {
      const now = latest.current;
      const next = neighbourOnClose(now.views, id, now.activeId);
      if (leavesSingleView(now.views, id)) {
        // Land on the one left (its address and lens), then drop the bar altogether.
        if (next) activateView(next.id);
        dispatch(clearViews());
        return;
      }
      dispatch(removeView(id));
      if (next) activateView(next.id);
    },
    [dispatch, activateView]
  );

  const moveView = useCallback(
    (id: string, toIndex: number) => dispatch(moveViewAction({ id, toIndex })),
    [dispatch]
  );

  // ------------------------------------------------------------- ownership

  useEffect(() => {
    if (ownerKey) dispatch(claimViews(ownerKey));
  }, [ownerKey, dispatch]);

  // ------------------------------------------- the active view follows the user

  useEffect(() => {
    const now = latest.current;
    if (now.views.length === 0) return;

    if (!isViewable) {
      // Settings / Admin Center: the user left the views. Nothing is written into the
      // view they came from, and none shows as current.
      if (now.activeId) dispatch(setActiveView(null));
      return;
    }

    // Back / Forward onto an address another view holds: that view is where the user
    // went, not something to overwrite the active one with. Views share one browser
    // history, so this is what keeps them from bleeding into each other.
    if (navigationType === "POP") {
      const owner = now.views.find((v) => v.url === url && v.id !== now.activeId);
      if (owner) {
        dispatch(setActiveView(owner.id));
        if (!sameSelection(owner.selection, now.selection)) dispatch(restoreSelection(owner.selection));
        return;
      }
    }

    if (now.activeId) {
      dispatch(setViewUrl({ id: now.activeId, url }));
      return;
    }

    const match = viewForLocation(now.views, url, now.selection);
    if (match && sameSelection(match.selection, now.selection)) dispatch(setActiveView(match.id));
  }, [url, isViewable, navigationType, dispatch]);

  // The selector moves only the active view's lens.
  useEffect(() => {
    const now = latest.current;
    if (!now.activeId || !now.isViewable) return;
    const active = now.views.find((v) => v.id === now.activeId);
    if (active && !sameSelection(active.selection, selection)) {
      dispatch(setViewSelection({ id: active.id, selection }));
    }
  }, [selection, dispatch]);

  // ---------------------------------------------------------------- labels

  const labelsInFlight = useRef(new Set<string>());
  useEffect(() => {
    for (const view of views) {
      const identity = viewIdentity(view.url, view.selection);
      if (view.labelFor === identity) continue;
      const key = `${view.id}|${identity}`;
      if (labelsInFlight.current.has(key)) continue;
      labelsInFlight.current.add(key);

      resolveViewLabel(api, view.url, view.selection.tenantId)
        .catch(() => null)
        .then((label) => {
          dispatch(setViewLabel({ id: view.id, label, labelFor: identity }));
        })
        .finally(() => labelsInFlight.current.delete(key));
    }
  }, [views, api, dispatch]);

  // -------------------------------------------------------------- gestures

  useEffect(() => {
    const onContextMenu = (event: MouseEvent) => {
      const found = viewTargetFrom(event.target);
      if (!found || hasSelectionInside(found.element)) return;
      event.preventDefault();
      setMenu({ href: found.href, x: event.clientX, y: event.clientY, returnFocus: found.element });
    };

    // Capture phase, ahead of React's own listener on the root: the element's onClick
    // must not also navigate.
    const onClick = (event: MouseEvent) => {
      if (event.button !== 0 || !(event.metaKey || event.ctrlKey)) return;
      const found = viewTargetFrom(event.target);
      if (!found) return;
      event.preventDefault();
      event.stopPropagation();
      openInView(found.href);
    };

    const onAuxClick = (event: MouseEvent) => {
      if (event.button !== 1) return;
      const found = viewTargetFrom(event.target);
      if (!found) return;
      event.preventDefault();
      event.stopPropagation();
      openInView(found.href);
    };

    // Middle-button down would otherwise start the browser's autoscroll.
    const onMouseDown = (event: MouseEvent) => {
      if (event.button === 1 && viewTargetFrom(event.target)) event.preventDefault();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const opensMenu = event.key === "ContextMenu" || (event.shiftKey && event.key === "F10");
      if (!opensMenu) return;
      const found = viewTargetFrom(document.activeElement);
      if (!found) return;
      event.preventDefault();
      const rect = found.element.getBoundingClientRect();
      setMenu({ href: found.href, x: rect.left, y: rect.bottom, returnFocus: found.element });
    };

    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onAuxClick, true);
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("auxclick", onAuxClick, true);
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openInView]);

  const dismissNotice = useCallback(() => setNotice(null), []);

  const value = useMemo<ViewsContextValue>(
    () => ({
      views,
      activeId,
      openInView,
      activateView,
      closeView,
      moveView,
      flash,
      notice,
      dismissNotice,
    }),
    [views, activeId, openInView, activateView, closeView, moveView, flash, notice, dismissNotice]
  );

  return (
    <ViewsContext.Provider value={value}>
      {children}
      {menu && (
        <ViewContextMenu
          anchor={menu}
          onAddView={() => openInView(menu.href, { focus: true })}
          onClose={() => setMenu(null)}
        />
      )}
    </ViewsContext.Provider>
  );
}
